import React, { useState } from 'react';
import { User, ChevronDown, ChevronRight, Heart, Pill, AlertTriangle, ShieldAlert } from 'lucide-react';
import type { PatientSummaryWidgetData } from '../../../types/agentWidgets';

interface Props {
  data: PatientSummaryWidgetData;
}

function formatDob(dob: string | null | undefined): string | null {
  if (!dob) return null;
  try {
    const d = new Date(dob);
    const age = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
    return `${d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} (age ${age})`;
  } catch {
    return dob;
  }
}

const ListSection: React.FC<{ icon: React.FC<{ className?: string }>; label: string; items: string[]; color: string }> = ({ icon: Icon, label, items, color }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-1 mb-1">
        <Icon className={`h-3 w-3 ${color}`} />
        <span className={`text-[10px] uppercase tracking-wide font-medium ${color}`}>{label}</span>
      </div>
      <div className="flex flex-wrap gap-1 ml-4">
        {items.map((item, i) => (
          <span key={i} className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-gray-100 dark:bg-medical-700 text-gray-700 dark:text-gray-300">
            {item}
          </span>
        ))}
      </div>
    </div>
  );
};

export const PatientSummaryWidget: React.FC<Props> = ({ data }) => {
  const [collapsed, setCollapsed] = useState(false);

  const dobStr = formatDob(data.dateOfBirth);
  const hasContent = data.conditions.length > 0 || data.medications.length > 0 || data.riskFactors.length > 0 || data.allergies.length > 0;

  return (
    <div className="bg-emerald-50/30 dark:bg-emerald-900/5 border-l-3 border-emerald-400 dark:border-emerald-600">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="flex items-center gap-2 w-full px-3 py-2 text-left hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 transition-colors"
      >
        {collapsed ? <ChevronRight className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />}
        <User className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
        <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
          {data.name}
        </span>
        {dobStr && (
          <span className="text-[10px] text-gray-500 dark:text-gray-400">
            {dobStr}
          </span>
        )}
      </button>

      {!collapsed && hasContent && (
        <div className="px-3 pb-3 space-y-2.5">
          <ListSection icon={Heart} label="Conditions" items={data.conditions} color="text-red-500 dark:text-red-400" />
          <ListSection icon={Pill} label="Medications" items={data.medications} color="text-cyan-500 dark:text-cyan-400" />
          <ListSection icon={AlertTriangle} label="Risk Factors" items={data.riskFactors} color="text-amber-500 dark:text-amber-400" />
          <ListSection icon={ShieldAlert} label="Allergies" items={data.allergies} color="text-rose-500 dark:text-rose-400" />
        </div>
      )}
    </div>
  );
};
